import useUpdateProject from '@/api/mutations/project/useUpdateProject';
import projectKeys from '@/api/queries/project/keyFactory';
import { ProjectByIdQuery } from '@/gql/graphql';
import { Combobox, SelectSizeVariant } from '@payaca/ambrosia';

import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../../api/queries/project/useGetProjectByIdOverview';

const PipelineControl: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data, project } = useGetProjectByIdOverview(projectId);
  const queryClient = useQueryClient();
  const { mutateAsync: updateProjectMutation } = useUpdateProject({
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(projectId),
      });

      // Snapshot the previous value
      const previousProjectOver = queryClient.getQueryData(
        projectKeys.overview(projectId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ProjectByIdQuery>(
        projectKeys.overview(projectId),
        (old) => {
          if (!old) return;

          let newSelectedPipeline = old.me.user.account.pipelines.find(
            (i) => i.id === variables.pipelineId
          );

          if (!newSelectedPipeline) {
            // Set as "Default" pipeline, which has the id of "0"
            newSelectedPipeline = old.me.user.account.pipelines.find(
              (i) => i.id === '0'
            );
          }

          if (!newSelectedPipeline) {
            // Should never get here
            return old;
          }

          return {
            ...old,
            projectById: {
              ...old.projectById,
              pipeline: newSelectedPipeline,
            },
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousProjectOver };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        projectKeys.overview(projectId),
        // @ts-expect-error
        context.previousProjectOver
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.project(projectId),
      });
    },
  });

  const selectedPipelineId = useMemo(() => {
    if (!project?.pipeline) return;

    return project.pipeline.id;
  }, [project]);

  const availablePipelineStages = useMemo(() => {
    if (!data || !data.me?.user?.account?.pipelines) return [];

    return data.me.user.account.pipelines
      .filter((pipeline) => pipeline.type !== 'SERVICE')
      .map((pipeline) => ({
        value: pipeline.id,
        label: pipeline.name,
        metadata: pipeline,
      }));
  }, [data]);

  const handleChange = useCallback(
    async (value: string | string[]) => {
      if (Array.isArray(value)) {
        // Should never get here
        return;
      }

      await updateProjectMutation({
        projectId: projectId,
        pipelineId: value === '0' ? null : value,
      });
    },
    [updateProjectMutation, projectId]
  );

  return (
    <Combobox
      CustomSelected={({ selectedOptions }) =>
        selectedOptions[0] ? (
          <div className="flex items-center gap-1.5">
            <b>Pipeline:</b>
            <div
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: selectedOptions[0].metadata?.colour }}
            />
            {selectedOptions[0].label}
          </div>
        ) : null
      }
      CustomOption={({ option }) => (
        <div className="flex items-center gap-1.5">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: option.metadata?.colour }}
          />
          {option.label}
        </div>
      )}
      sizeVariant={SelectSizeVariant.SM}
      value={selectedPipelineId}
      options={availablePipelineStages}
      onChange={handleChange}
    />
  );
};

export default PipelineControl;
